import { memo, useCallback } from 'react';
import { Paper, Typography, Box, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { OperatorValue, QueryField, QueryRule } from '../../../../types/QueryBuilder';
import { useLanguage } from '../../../../context/LanguageContext';
import { isRuleValid } from '../../../../utils/queryBuilderUtils';
import ValueEditor from './ValueEditors';
import StoreListImport from './StoreListImport';
import PurchasePeriodQuickSelect from './PurchasePeriodQuickSelect';

interface SimpleQueryGroupProps {
    fields: QueryField[];
    rules: QueryRule[];
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}

interface FieldRowProps {
    field: QueryField;
    rule: QueryRule | undefined;
    onUpdateField: (fieldName: string, data: Partial<QueryRule>) => void;
}

const OTHER_CATEGORY_KEY = 'pages.clients.queryBuilder.simple.categories.other';

// Field whose `values` list the store-list import button pre-selects by identifier.
const STORE_FIELD_NAME = 'PERSON_ENTITY';

// Field the "last N months" quick-select button pre-fills with a between-dates range.
const PURCHASE_PERIOD_FIELD_NAME = 'TRANSACTION_PERIOD';

// Fields that get an explicit IN / NOT IN toggle, since "belongs to" vs. "does not belong to"
// changes the meaning of the selected identifiers rather than just filtering them (default: IN).
const IN_NOT_IN_TOGGLE_FIELD_NAMES = new Set([
    'CUSTOMER_IDENTIFIER',
    'CUSTOMER_LIST_BY_IDENTIFIER',
    'CUSTOMER_LIST_BY_CARTE',
]);

// Field `name` identifiers returned by /getDataQuerySegmentList, grouped by business meaning.
// Fields whose name isn't listed here fall back into the "other" bucket.
const SUBCATEGORIES: { titleKey: string; fieldNames: string[] }[] = [
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.clientCategory',
        fieldNames: ['PERSON_ENTITY', 'CUSTOMER_IDENTIFIER',  'CUSTOMER_ACCOUNT', 'CUSTOMER_ACCOUNT_TYPE', 'CUSTOMER_ACTIVATION_DATE'],
    },
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.clientScore',
        fieldNames: ['CUSTOMER_SCORE', 'CUSTOMER_SCORE_BEST', 'CUSTOMER_SUBSCRIPTIONS_TYPE'],
    },
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.purchaseBehavior',
        fieldNames: ['TRANSACTION_NB', 'TRANSACTION_PERIOD'],
    },
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.gdpr',
        fieldNames: [
            'CUSTOMER_OPTIN_EMAIL', 'CUSTOMER_STATUS_EMAIL',
            'CUSTOMER_OPTIN_SMS', 'CUSTOMER_STATUS_SMS',
            'CUSTOMER_OPTIN_COURRIER', 'CUSTOMER_STATUS_COURRIER',
        ],
    },
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.geography',
        fieldNames: ['CUSTOMER_CITY', 'CUSTOMER_DEPARTMENT', 'CUSTOMER_COUNTRY'],
    },
    {
        titleKey: 'pages.clients.queryBuilder.simple.categories.clientLists',
        fieldNames: ['CUSTOMER_LIST_BY_IDENTIFIER', 'CUSTOMER_LIST_BY_CARTE'],
    },
];

const FIELD_NAME_TO_CATEGORY_KEY = new Map<string, string>();
const FIELD_NAME_TO_ORDER = new Map<string, number>();
SUBCATEGORIES.forEach(({ titleKey, fieldNames }) => {
    fieldNames.forEach((name, index) => {
        FIELD_NAME_TO_CATEGORY_KEY.set(name, titleKey);
        FIELD_NAME_TO_ORDER.set(name, index);
    });
});

function groupFieldsBySubcategory(fields: QueryField[]): { titleKey: string; fields: QueryField[] }[] {
    const order = [...SUBCATEGORIES.map(s => s.titleKey), OTHER_CATEGORY_KEY];
    const buckets = new Map<string, QueryField[]>(order.map(titleKey => [titleKey, []]));

    fields.forEach(field => {
        const titleKey = FIELD_NAME_TO_CATEGORY_KEY.get(field.name) ?? OTHER_CATEGORY_KEY;
        buckets.get(titleKey)!.push(field);
    });

    buckets.forEach(bucketFields => {
        bucketFields.sort((a, b) => (FIELD_NAME_TO_ORDER.get(a.name) ?? 0) - (FIELD_NAME_TO_ORDER.get(b.name) ?? 0));
    });

    return order
        .map(titleKey => ({ titleKey, fields: buckets.get(titleKey) ?? [] }))
        .filter(group => group.fields.length > 0);
}

// The help text for a category lives at the same key path as its title, under "categoryHelp" instead of "categories".
const toHelpKey = (titleKey: string): string => titleKey.replace('.categories.', '.categoryHelp.');

type Translate = (key: string, params?: Record<string, string | number>) => string;

// One rendered block per filled-in field: an optional bold mini-title (e.g. the field label),
// an optional connector phrase ("appartient à :"), and one or more value lines shown below it.
interface SummaryField {
    id: string;
    label?: string;
    intro?: string;
    values: string[];
}

// Maps operator values to the translation key of their human-readable label; '=' has no prefix (implicit equality).
const OPERATOR_LABEL_KEY: Partial<Record<OperatorValue, string>> = {
    '!=': 'notEquals',
    '>': 'greaterThan',
    '<': 'lessThan',
    '>=': 'greaterOrEqual',
    '<=': 'lessOrEqual',
    between: 'between',
    in: 'in',
    'not in': 'notIn',
    contains: 'contains',
    'not contains': 'notContains',
};

const resolveValueLabel = (field: QueryField, value: unknown): string =>
    field.values?.find(fv => fv.name === String(value))?.label ?? String(value);

const toValueList = (value: QueryRule['value']): string[] => {
    if (Array.isArray(value)) return value.map(String);
    if (value === null || value === undefined || value === '') return [];
    return [String(value)];
};

const parseBetween = (rule: QueryRule): [string, string] => {
    const raw = String(rule.value ?? '');
    if (raw.includes('|')) {
        const [min, max] = raw.split('|');
        return [min || '', max || ''];
    }
    return [raw, String(rule.valueTo ?? '')];
};

const formatDateValue = (raw: string, formatLocale: string): string => {
    if (!raw) return '?';
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? raw : date.toLocaleDateString(formatLocale);
};

// Resolves the current (comma-joined) label(s) of another field's rule, e.g. reading the selected
// card/subscription type from CUSTOMER_ACCOUNT_TYPE to caption the CUSTOMER_SCORE counter with it.
const resolveFieldValueLabel = (fields: QueryField[], rules: QueryRule[], fieldName: string): string => {
    const field = fields.find(f => f.name === fieldName);
    const rule = rules.find(r => r.field === fieldName);
    if (!field || !rule || !isRuleValid(rule)) return '';
    return toValueList(rule.value).map(v => resolveValueLabel(field, v)).join(', ');
};

// Fallback used for any field without a dedicated phrasing below: "Field label : operator value(s)".
function genericSummaryField(field: QueryField, rule: QueryRule, t: Translate): SummaryField {
    const operatorKey = rule.operator ? OPERATOR_LABEL_KEY[rule.operator] : undefined;
    const operatorLabel = operatorKey ? `${t(`pages.clients.queryBuilder.operators.${operatorKey}`)} ` : '';

    let value: string;
    if (rule.operator === 'between') {
        const [min, max] = parseBetween(rule);
        value = t('pages.clients.queryBuilder.simple.summary.betweenValue', { min: min || '?', max: max || '?' });
    } else if (Array.isArray(rule.value)) {
        value = rule.value.map(v => resolveValueLabel(field, v)).join(', ');
    } else if (field.valueEditorType === 'checkbox' && !field.values?.length) {
        value = t('pages.clients.queryBuilder.simple.summary.yes');
    } else {
        value = resolveValueLabel(field, rule.value);
    }

    return { id: rule.id, values: [t('pages.clients.queryBuilder.simple.summary.item', { field: field.label, operator: operatorLabel, value })] };
}

// A single-sentence sender ("carte ouverte entre le X et le Y") with no separate header/intro line.
const sentenceField = (rule: QueryRule, sentence: string): SummaryField => ({ id: rule.id, values: [sentence] });

// A boolean opt-in field ('1' = accepts, '0' = refuses); rules with no value are filtered out before reaching here.
const optinSummaryField = (rule: QueryRule, t: Translate, acceptKey: string, refuseKey: string): SummaryField =>
    sentenceField(rule, t(String(rule.value) === '0' ? refuseKey : acceptKey));

// A field whose value is a list of options, rendered as a connector phrase followed by one value per line.
function listField(field: QueryField, rule: QueryRule, t: Translate, intro: string, withLabel = true): SummaryField {
    return {
        id: rule.id,
        label: withLabel ? field.label : undefined,
        intro,
        values: toValueList(rule.value).map(v => resolveValueLabel(field, v)),
    };
}

// Field `name` identifiers (see SUBCATEGORIES above) get bespoke, natural-language phrasing matching
// how sales teams read a segmentation recap. Anything else (or an unexpected operator) falls back to
// the generic "label : operator value" line via genericSummaryField.
const FIELD_SUMMARY_BUILDERS: Record<string, (field: QueryField, rule: QueryRule, t: Translate, formatLocale: string, fields: QueryField[], rules: QueryRule[]) => SummaryField | null> = {
    PERSON_ENTITY: (field, rule, t) => listField(
        field, rule, t,
        t(rule.operator === 'not in' ? 'pages.clients.queryBuilder.simple.summary.storesNotBelong' : 'pages.clients.queryBuilder.simple.summary.storesBelong'),
        false
    ),
    CUSTOMER_IDENTIFIER: (field, rule, t) => listField(
        field, rule, t,
        `${t(`pages.clients.queryBuilder.operators.${rule.operator === 'not in' ? 'notIn' : 'in'}`)} :`
    ),
    CUSTOMER_LIST_BY_IDENTIFIER: (field, rule, t) => listField(
        field, rule, t,
        `${t(`pages.clients.queryBuilder.operators.${rule.operator === 'not in' ? 'notIn' : 'in'}`)} :`
    ),
    CUSTOMER_LIST_BY_CARTE: (field, rule, t) => listField(
        field, rule, t,
        `${t(`pages.clients.queryBuilder.operators.${rule.operator === 'not in' ? 'notIn' : 'in'}`)} :`
    ),
    CUSTOMER_SUBSCRIPTIONS_TYPE: (field, rule, t) => listField(
        field, rule, t,
        `${t(`pages.clients.queryBuilder.operators.${rule.operator === 'not in' ? 'notIn' : 'in'}`)} :`
    ),
    CUSTOMER_CITY: (field, rule, t) => listField(
        field, rule, t,
        t(rule.operator === 'not in' ? 'pages.clients.queryBuilder.simple.summary.doesNotLiveIn' : 'pages.clients.queryBuilder.simple.summary.livesIn')
    ),
    CUSTOMER_DEPARTMENT: (field, rule, t) => listField(
        field, rule, t,
        t(rule.operator === 'not in' ? 'pages.clients.queryBuilder.simple.summary.doesNotLiveIn' : 'pages.clients.queryBuilder.simple.summary.livesIn')
    ),
    CUSTOMER_COUNTRY: (field, rule, t) => listField(
        field, rule, t,
        t(rule.operator === 'not in' ? 'pages.clients.queryBuilder.simple.summary.doesNotLiveIn' : 'pages.clients.queryBuilder.simple.summary.livesIn')
    ),
    CUSTOMER_ACCOUNT: (_field, rule, t, _formatLocale, fields, rules) => {
        if (rule.operator !== 'between') return null;
        const [min, max] = parseBetween(rule);
        const accountType = resolveFieldValueLabel(fields, rules, 'CUSTOMER_ACCOUNT_TYPE') || t('pages.clients.queryBuilder.simple.summary.genericCounterType');
        return sentenceField(rule, t('pages.clients.queryBuilder.simple.summary.scorePointsBetween', {
            accountType: accountType, min: min, max: max,
        }));
    },
    CUSTOMER_SCORE: (field, rule, t) => listField(field, rule, t, ''),
    CUSTOMER_ACTIVATION_DATE: (_field, rule, t, formatLocale) => {
        if (rule.operator !== 'between') return null;
        const [min, max] = parseBetween(rule);
        return sentenceField(rule, t('pages.clients.queryBuilder.simple.summary.cardOpenedBetween', {
            min: formatDateValue(min, formatLocale), max: formatDateValue(max, formatLocale),
        }));
    },
    CUSTOMER_SCORE_BEST: (_field, rule, t) => {
        if (rule.operator !== '<') return null;
        return sentenceField(rule, t('pages.clients.queryBuilder.simple.summary.topClients', { value: String(rule.value ?? '') }));
    },
    TRANSACTION_PERIOD: (_field, rule, t, formatLocale) => {
        if (rule.operator !== 'between') return null;
        const [min, max] = parseBetween(rule);
        return sentenceField(rule, t('pages.clients.queryBuilder.simple.summary.purchasePeriodBetween', {
            min: formatDateValue(min, formatLocale), max: formatDateValue(max, formatLocale),
        }));
    },
    TRANSACTION_NB: (_field, rule, t) => {
        if (rule.operator !== '>' && rule.operator !== '>=') return null;
        return sentenceField(rule, t('pages.clients.queryBuilder.simple.summary.minPurchases', { value: String(rule.value ?? '') }));
    },
    CUSTOMER_OPTIN_EMAIL: (_field, rule, t) => optinSummaryField(
        rule, t, 'pages.clients.queryBuilder.simple.summary.optinEmail', 'pages.clients.queryBuilder.simple.summary.optinEmailRefuse'
    ),
    CUSTOMER_OPTIN_SMS: (_field, rule, t) => optinSummaryField(
        rule, t, 'pages.clients.queryBuilder.simple.summary.optinSms', 'pages.clients.queryBuilder.simple.summary.optinSmsRefuse'
    ),
    CUSTOMER_OPTIN_COURRIER: (_field, rule, t) => optinSummaryField(
        rule, t, 'pages.clients.queryBuilder.simple.summary.optinCourrier', 'pages.clients.queryBuilder.simple.summary.optinCourrierRefuse'
    ),
    CUSTOMER_STATUS_EMAIL: (field, rule, t) => listField(field, rule, t, ''),
    CUSTOMER_STATUS_SMS: (field, rule, t) => listField(field, rule, t, ''),
    CUSTOMER_STATUS_COURRIER: (field, rule, t) => listField(field, rule, t, ''),
};

function buildSummaryField(field: QueryField, rule: QueryRule, t: Translate, formatLocale: string, fields: QueryField[], rules: QueryRule[]): SummaryField {
    const built = FIELD_SUMMARY_BUILDERS[field.name]?.(field, rule, t, formatLocale, fields, rules);
    return built ?? genericSummaryField(field, rule, t);
}

// Fields whose selection is never shown as its own summary line, either because another field's
// sentence already reads its value (e.g. CUSTOMER_ACCOUNT_TYPE captions the CUSTOMER_ACCOUNT counter)
// or because it carries no user-facing meaning on its own.
const HIDDEN_FROM_SUMMARY_FIELD_NAMES = new Set(['CUSTOMER_ACCOUNT_TYPE']);

const SelectionSummary = memo(function SelectionSummary({ fields, rules }: { fields: QueryField[]; rules: QueryRule[] }) {
    const { t, formatLocale } = useLanguage();

    const summaryGroups = groupFieldsBySubcategory(fields)
        .map(({ titleKey, fields: groupFields }) => ({
            titleKey,
            items: groupFields
                .filter(field => !HIDDEN_FROM_SUMMARY_FIELD_NAMES.has(field.name))
                .map(field => {
                    const rule = rules.find(r => r.field === field.name);
                    return rule && isRuleValid(rule) ? buildSummaryField(field, rule, t, formatLocale, fields, rules) : null;
                })
                .filter((item): item is SummaryField => item !== null),
        }))
        .filter(group => group.items.length > 0);

    return (
        <Box sx={{ mt:2, mb: 2, p: 1.5, bgcolor: '#eef1fd', border: '1px solid #c7d0f8', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#3949ab' }}>
                {t('pages.clients.queryBuilder.simple.summary.title')}
            </Typography>
            {summaryGroups.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#6c757d', mt: 0.5 }}>
                    {t('pages.clients.queryBuilder.simple.summary.empty')}
                </Typography>
            ) : (
                summaryGroups.map(group => (
                    <Box key={group.titleKey} sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#3949ab' }}>
                            {t(group.titleKey)}.
                        </Typography>
                        {group.items.map(item => (
                            <Box key={item.id} sx={{ mt: 0.5 }}>
                                {item.label && (
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#495057' }}>
                                        {item.label}
                                    </Typography>
                                )}
                                {item.intro && (
                                    <Typography variant="body2" sx={{ color: '#495057' }}>
                                        {item.intro}
                                    </Typography>
                                )}
                                {item.values.map((value, index) => (
                                    <Typography
                                        key={index}
                                        variant="body2"
                                        sx={{ color: '#495057', pl: item.label || item.intro ? 2 : 0 }}
                                    >
                                        {value}
                                    </Typography>
                                ))}
                            </Box>
                        ))}
                    </Box>
                ))
            )}
        </Box>
    );
});

const FieldRow = memo(function FieldRow({ field, rule, onUpdateField }: FieldRowProps) {
    const { t } = useLanguage();

    const handleValueChange = useCallback(
        (value: QueryRule['value']) => onUpdateField(field.name, { value }),
        [field.name, onUpdateField]
    );

    const handleApplyPeriod = useCallback(
        (value: string) => onUpdateField(field.name, { operator: 'between', value }),
        [field.name, onUpdateField]
    );

    const handleOperatorChange = useCallback(
        (operator: OperatorValue | null) => operator && onUpdateField(field.name, { operator }),
        [field.name, onUpdateField]
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                p: 1.5,
                bgcolor: '#ffffff',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
                '&:hover': {
                    borderColor: '#556ee6',
                    boxShadow: '0 2px 8px rgba(85, 110, 230, 0.1)'
                }
            }}
        >
            <Typography variant="body2" sx={{ minWidth: 160, fontWeight: 500, color: '#495057' }}>
                {field.label}
            </Typography>
            {IN_NOT_IN_TOGGLE_FIELD_NAMES.has(field.name) && (
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={rule?.operator || field.defaultOperator || 'in'}
                    onChange={(_event, operator) => handleOperatorChange(operator)}
                >
                    <ToggleButton value="in">{t('pages.clients.queryBuilder.operators.in')}</ToggleButton>
                    <ToggleButton value="not in">{t('pages.clients.queryBuilder.operators.notIn')}</ToggleButton>
                </ToggleButtonGroup>
            )}
            <Box sx={{ flexGrow: 1, minWidth: '200px' }}>
                <ValueEditor
                    ruleId={rule?.id || field.name}
                    field={field}
                    operator={rule?.operator || field.defaultOperator || '='}
                    value={rule?.value}
                    onChange={handleValueChange}
                />
            </Box>
            {field.name === STORE_FIELD_NAME && (
                <StoreListImport
                    values={field.values || []}
                    selectedValue={rule?.value}
                    onChange={handleValueChange}
                />
            )}
            {field.name === PURCHASE_PERIOD_FIELD_NAME && (
                <PurchasePeriodQuickSelect onApply={handleApplyPeriod} />
            )}
        </Box>
    );
});

function SimpleQueryGroup({ fields, rules, onUpdateField }: SimpleQueryGroupProps) {
    const { t } = useLanguage();
    const subcategoryGroups = groupFieldsBySubcategory(fields);

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 2,
                bgcolor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: 2
            }}
        >
            <Typography variant="subtitle2" sx={{ color: '#6c757d', fontWeight: 600, mb: 2 }}>
                {t('pages.clients.queryBuilder.simple.searchCriteria')}
            </Typography>

            {fields.length > 0 && <SelectionSummary fields={fields} rules={rules} />}

            {fields.length === 0 ? (
                <Box
                    sx={{
                        p: 3,
                        textAlign: 'center',
                        color: '#6c757d',
                        bgcolor: 'white',
                        borderRadius: 1,
                        border: '2px dashed #dee2e6'
                    }}
                >
                    <Typography variant="body2">
                        {t('pages.clients.queryBuilder.simple.noFieldsAvailable')}
                    </Typography>
                </Box>
            ) : (
                subcategoryGroups.map(({ titleKey, fields: groupFields }, index) => (
                    <Box key={titleKey} sx={{ mb: index < subcategoryGroups.length - 1 ? 3 : 0 }}>
                        <Typography
                            variant="body2"
                            sx={{ color: '#556ee6', fontWeight: 700, mb: 0.5, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}
                        >
                            {t(titleKey)}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#6c757d', mb: 1 }}>
                            {t(toHelpKey(titleKey))}
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: 1.5
                            }}
                        >
                            {groupFields.map((field) => {
                                const rule = rules.find(r => r.field === field.name);
                                return (
                                    <FieldRow
                                        key={field.name}
                                        field={field}
                                        rule={rule}
                                        onUpdateField={onUpdateField}
                                    />
                                );
                            })}
                        </Box>
                    </Box>
                ))
            )}

            {fields.length > 0 && <SelectionSummary fields={fields} rules={rules} />}
        </Paper>
    );
}

export default SimpleQueryGroup;
